/**
 * Create Training Job Page
 *
 * Form to create a new training job by selecting a dataset,
 * annotation set, and configuring training parameters.
 */

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  CheckboxGroup,
  Input,
  Select,
  SelectItem,
  Spinner,
} from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, Database, Settings, Tag } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useDatasets, useDataset } from '@/lib/api/hooks/datasets';
import { useAnnotationSets } from '@/lib/api/hooks/annotations';
import { useCreateTrainingJob } from '@/lib/api/hooks/training';

const trainingConfigSchema = z.object({
  dataset_id: z.string().min(1, 'Dataset is required'),
  annotation_set_id: z.string().min(1, 'Annotation set is required'),
  artifact_types: z.array(z.string()).min(1, 'Select at least one artifact type'),
  epochs: z.number().min(1).max(100),
  learning_rate: z.number().min(0.00001).max(0.1),
  batch_size: z.number().min(1).max(128),
  validation_split: z.number().min(0.05).max(0.5),
  chunk_size_ms: z.number().min(100).max(2000),
  stride_ms: z.number().min(50).max(1000),
});

type TrainingFormData = z.infer<typeof trainingConfigSchema>;

export const Route = createFileRoute('/training/new')({
  component: NewTrainingJobPage,
});

function NewTrainingJobPage() {
  const navigate = useNavigate();
  const createTrainingJob = useCreateTrainingJob();
  const { data: datasets, isLoading: isLoadingDatasets } = useDatasets();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<TrainingFormData>({
    resolver: zodResolver(trainingConfigSchema),
    defaultValues: {
      dataset_id: '',
      annotation_set_id: '',
      artifact_types: [],
      epochs: 10,
      learning_rate: 0.001,
      batch_size: 32,
      validation_split: 0.2,
      chunk_size_ms: 500,
      stride_ms: 250,
    },
  });

  const selectedDatasetId = watch('dataset_id');
  const selectedAnnotationSetId = watch('annotation_set_id');

  // Fetch dataset details when selected
  const { data: selectedDataset } = useDataset(selectedDatasetId);

  // Fetch annotation sets for selected dataset
  const { data: annotationSets, isLoading: isLoadingAnnotationSets } =
    useAnnotationSets(selectedDatasetId);

  // Filter to only published annotation sets
  const publishedAnnotationSets = useMemo(
    () => annotationSets?.items?.filter((set) => set.status === 'published') ?? [],
    [annotationSets]
  );

  // Get artifact types from selected dataset
  const artifactTypes = useMemo(() => {
    if (!selectedDataset?.artifact_types) return [];
    return selectedDataset.artifact_types.map((at) => ({
      name: (at as { name?: string }).name || '',
      color: (at as { color?: string }).color || '#888888',
    }));
  }, [selectedDataset]);

  // Reset annotation set and artifact types when dataset changes
  useEffect(() => {
    if (selectedDatasetId) {
      setValue('annotation_set_id', '');
      setValue('artifact_types', []);
    }
  }, [selectedDatasetId, setValue]);

  // Auto-select all artifact types when dataset changes
  useEffect(() => {
    if (artifactTypes.length > 0) {
      setValue(
        'artifact_types',
        artifactTypes.map((at) => at.name)
      );
    }
  }, [artifactTypes, setValue]);

  const onSubmit = async (data: TrainingFormData) => {
    try {
      const result = await createTrainingJob.mutateAsync({
        dataset_id: data.dataset_id,
        annotation_set_id: data.annotation_set_id,
        config: {
          artifact_types: data.artifact_types,
          epochs: data.epochs,
          learning_rate: data.learning_rate,
          batch_size: data.batch_size,
          validation_split: data.validation_split,
          chunk_size_ms: data.chunk_size_ms,
          stride_ms: data.stride_ms,
        },
      });
      if (result?.id) {
        navigate({ to: '/training/$jobId', params: { jobId: result.id } });
      }
    } catch {
      // Error is handled by the mutation
    }
  };

  const selectedAnnotationSet = publishedAnnotationSets.find(
    (set) => set.id === selectedAnnotationSetId
  );

  return (
    <div className="container-page max-w-2xl py-4">
      <div className="mb-6">
        <Button
          variant="light"
          size="sm"
          startContent={<ArrowLeft className="h-4 w-4" />}
          onPress={() => navigate({ to: '/training' })}
          className="mb-4"
        >
          Back to Training Jobs
        </Button>
        <h1>New Training Job</h1>
        <p className="text-default-500">
          Train a new artifact detection model from your labeled data.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Dataset Selection */}
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Database className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col">
              <h2>Select Dataset</h2>
              <p className="text-xs text-default-500">
                Choose the dataset containing your labeled audio
              </p>
            </div>
          </CardHeader>
          <CardBody className="space-y-3 px-4 pb-4 pt-2">
            {isLoadingDatasets ? (
              <div className="flex items-center justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : datasets && datasets.length > 0 ? (
              <Controller
                name="dataset_id"
                control={control}
                render={({ field }) => (
                  <Select
                    size="sm"
                    label="Dataset"
                    labelPlacement="outside"
                    placeholder="Select a dataset"
                    selectedKeys={field.value ? [field.value] : []}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0] as string;
                      field.onChange(selected || '');
                    }}
                    isInvalid={!!errors.dataset_id}
                    errorMessage={errors.dataset_id?.message}
                    isRequired
                  >
                    {datasets.map((dataset) => (
                      <SelectItem key={dataset.id} textValue={dataset.name}>
                        <div className="flex flex-col">
                          <span className="text-sm">{dataset.name}</span>
                          {dataset.description && (
                            <span className="text-xs text-default-400">
                              {dataset.description}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                )}
              />
            ) : (
              <div className="flex items-center gap-3 rounded-lg border border-default-200 bg-default-50 p-4">
                <AlertCircle className="h-5 w-5 text-warning" />
                <div className="flex-1">
                  <p className="text-sm font-medium">No datasets found</p>
                  <p className="text-xs text-default-500">
                    Create a dataset and upload audio files first.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => navigate({ to: '/datasets/new' })}
                >
                  Create Dataset
                </Button>
              </div>
            )}

            {/* Annotation Set Selection */}
            {selectedDatasetId && (
              <>
                {isLoadingAnnotationSets ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : publishedAnnotationSets.length > 0 ? (
                  <Controller
                    name="annotation_set_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        size="sm"
                        label="Annotation Set"
                        labelPlacement="outside"
                        placeholder="Select an annotation set"
                        selectedKeys={field.value ? [field.value] : []}
                        onSelectionChange={(keys) => {
                          const selected = Array.from(keys)[0] as string;
                          field.onChange(selected || '');
                        }}
                        isInvalid={!!errors.annotation_set_id}
                        errorMessage={errors.annotation_set_id?.message}
                        isRequired
                      >
                        {publishedAnnotationSets.map((set) => (
                          <SelectItem key={set.id} textValue={`Version ${set.version}`}>
                            <div className="flex flex-col">
                              <span className="text-sm">Version {set.version}</span>
                              <span className="text-xs text-default-400">
                                {set.total_annotations} annotations •{' '}
                                {(set.total_positive_duration_ms / 1000).toFixed(1)}s labeled
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                  />
                ) : (
                  <div className="flex items-center gap-3 rounded-lg border border-default-200 bg-default-50 p-4">
                    <AlertCircle className="h-5 w-5 text-warning" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">No published annotation sets</p>
                      <p className="text-xs text-default-500">
                        Create annotations and publish them to use for training.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Selected Annotation Set Info */}
            {selectedAnnotationSet && (
              <div className="rounded-lg bg-success/10 p-3">
                <p className="text-xs text-success">
                  Using {selectedAnnotationSet.total_annotations} annotations with{' '}
                  {(selectedAnnotationSet.total_positive_duration_ms / 1000).toFixed(1)}s of
                  labeled audio
                </p>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Artifact Types Selection */}
        {selectedDatasetId && artifactTypes.length > 0 && (
          <Card className="border border-default-100 shadow-none">
            <CardHeader className="gap-3 pb-1 pt-3 px-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/10">
                <Tag className="h-4 w-4 text-secondary" />
              </div>
              <div className="flex flex-col">
                <h2>Artifact Types</h2>
                <p className="text-xs text-default-500">
                  Select which artifact types to train detection for
                </p>
              </div>
            </CardHeader>
            <CardBody className="px-4 pb-4 pt-2">
              <Controller
                name="artifact_types"
                control={control}
                render={({ field }) => (
                  <CheckboxGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    isInvalid={!!errors.artifact_types}
                    errorMessage={errors.artifact_types?.message}
                  >
                    <div className="grid grid-cols-2 gap-2">
                      {artifactTypes.map((at) => (
                        <Checkbox
                          key={at.name}
                          value={at.name}
                          classNames={{
                            base: 'inline-flex max-w-full w-full m-0 bg-content2 hover:bg-content3 items-center justify-start cursor-pointer rounded-lg gap-2 p-3 border border-default-100',
                            label: 'w-full',
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: at.color }}
                            />
                            <span className="text-sm">{at.name}</span>
                          </div>
                        </Checkbox>
                      ))}
                    </div>
                  </CheckboxGroup>
                )}
              />
            </CardBody>
          </Card>
        )}

        {/* Training Configuration */}
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-default-100">
              <Settings className="h-4 w-4 text-default-500" />
            </div>
            <div className="flex flex-col">
              <h2>Training Configuration</h2>
              <p className="text-xs text-default-500">
                Adjust model training parameters (defaults work well for most cases)
              </p>
            </div>
          </CardHeader>
          <CardBody className="space-y-4 px-4 pb-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <Input
                size="sm"
                type="number"
                label="Epochs"
                labelPlacement="outside"
                description="Training iterations"
                {...register('epochs', { valueAsNumber: true })}
                isInvalid={!!errors.epochs}
                errorMessage={errors.epochs?.message}
              />
              <Input
                size="sm"
                type="number"
                step="0.0001"
                label="Learning Rate"
                labelPlacement="outside"
                description="Model learning speed"
                {...register('learning_rate', { valueAsNumber: true })}
                isInvalid={!!errors.learning_rate}
                errorMessage={errors.learning_rate?.message}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                size="sm"
                type="number"
                label="Batch Size"
                labelPlacement="outside"
                description="Samples per batch"
                {...register('batch_size', { valueAsNumber: true })}
                isInvalid={!!errors.batch_size}
                errorMessage={errors.batch_size?.message}
              />
              <Input
                size="sm"
                type="number"
                step="0.01"
                label="Validation Split"
                labelPlacement="outside"
                description="Held-out validation %"
                {...register('validation_split', { valueAsNumber: true })}
                isInvalid={!!errors.validation_split}
                errorMessage={errors.validation_split?.message}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                size="sm"
                type="number"
                label="Chunk Size (ms)"
                labelPlacement="outside"
                description="Audio chunk length"
                {...register('chunk_size_ms', { valueAsNumber: true })}
                isInvalid={!!errors.chunk_size_ms}
                errorMessage={errors.chunk_size_ms?.message}
              />
              <Input
                size="sm"
                type="number"
                label="Stride (ms)"
                labelPlacement="outside"
                description="Chunk overlap"
                {...register('stride_ms', { valueAsNumber: true })}
                isInvalid={!!errors.stride_ms}
                errorMessage={errors.stride_ms?.message}
              />
            </div>
          </CardBody>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="flat"
            size="sm"
            onPress={() => navigate({ to: '/training' })}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            size="sm"
            isLoading={isSubmitting || createTrainingJob.isPending}
            isDisabled={!selectedDatasetId || !selectedAnnotationSetId}
          >
            Start Training
          </Button>
        </div>
      </form>
    </div>
  );
}
